import { TUint64 } from "@pro/common/contracts/atomicassets";
import { TransactResult } from "eosjs/dist/eosjs-api-interfaces";
import { Authorization } from "eosjs/dist/eosjs-serialize";
import { EosApi, EosUtils, IEosAction, IFindTableParams } from "../../eos";
import { ConfigRecord, PackRecord, StakeRecord, TActionName, TActionParam } from "./tb_types";

export class TBContract
{
	private readonly _api: EosApi;
	private readonly _account: string;

	constructor(api: EosApi, account: string)
	{
		this._account = account;
		this._api = api;
	}

	action<N extends TActionName>(name: N,
	                              auth: Authorization | Authorization[],
	                              data: TActionParam<N>, {broadcast = true} = {}): Promise<TransactResult>
	{
		return this._api.transact([this.makeAction(name, auth, data)], {broadcast});
	}

	makeAction<N extends TActionName>(name: N,
	                                  auth: Authorization | Authorization[],
	                                  data: TActionParam<N>): IEosAction<TActionParam<N>>
	{
		return {
			name: name,
			account: this._account,
			authorization: EosUtils.castAuth(auth),
			data: data,
		};
	}

	getStakedAssets(owner: string, params: Pick<IFindTableParams, "lower_bound" | "upper_bound" | "reverse"> = {})
	{
		return this._api.getFullTable<StakeRecord>({
			code: this._account,
			scope: owner,
			table: "stake",
			key_type: "i64",
			...params,
		});
	}

	getStakedAsset(owner: string, asset_id: TUint64): Promise<StakeRecord>
	{
		return this._api.getRecord(asset_id, {
			code: this._account,
			scope: owner,
			table: "stake",
			key_type: "i64",
		});
	}

	findStakedAsset(owner: string, asset_id: TUint64): Promise<StakeRecord | undefined>
	{
		return this._api.findRecord(asset_id, {
			code: this._account,
			scope: owner,
			table: "stake",
			key_type: "i64",
		});
	}

	getPacks(params: Pick<IFindTableParams, "lower_bound" | "upper_bound" | "reverse"> = {})
	{
		return this._api.getFullTable<PackRecord>({
			code: this._account,
			scope: this._account,
			table: "packs",
			key_type: "name",
			...params,
		});
	}

	findPack(account: string): Promise<PackRecord | undefined>
	{
		return this._api.findRecord(account, {
			code: this._account,
			scope: this._account,
			table: "packs",
			key_type: "name",
		});
	}

	getPack(account: string): Promise<PackRecord>
	{
		return this._api.getRecord(account, {
			code: this._account,
			scope: this._account,
			table: "packs",
			key_type: "name",
		});
	}

	async getConfig(): Promise<ConfigRecord>
	{
		let table = await this._api.getTable<ConfigRecord>({
			code: this._account,
			scope: this._account,
			table: "config",
			key_type: "i64",
			limit: 1,
		});

		if (table.rows.length === 0)
			throw new Error("Config not fount!");

		return table.rows[0];
	}

	get account()
	{
		return this._account;
	}

	get active(): Authorization
	{
		return {actor: this._account, permission: "active"};
	}

	get api()
	{
		return this._api;
	}

	// noinspection JSUnusedGlobalSymbols
	static makePack(props: Partial<PackRecord>)
	{
		let rec: PackRecord = {
			account: "",
			asset_id: 0,
			time: 0,
			r1: 0,
			r2: 0,
		};
		return {...rec, ...props};
	}
}
